import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {PeopleIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback, useRef} from 'react'
import {graphql, readInlineData, useLazyLoadQuery} from 'react-relay'
import {ConnectionHandler} from 'relay-runtime'

import {type BulkAssigneePickerProps, BulkIssuesAssigneePicker} from '../bulk-pickers/BulkIssuesAssigneePicker'
import type {SharedListHeaderActionProps} from '../types'
import {mergeRefs} from '../utils/merge-refs'
import type {ApplyAssigneesBulkActionIssueFragment$data} from './__generated__/ApplyAssigneesBulkActionIssueFragment.graphql'
import type {ApplyAssigneesBulkActionQuery} from './__generated__/ApplyAssigneesBulkActionQuery.graphql'

type ApplyAssigneesActionProps = {
  issueIds: string[]
  issueNumbers: number[]
} & SharedListHeaderActionProps

type ApplyAssigneesActionInternalProps = Omit<ApplyAssigneesActionProps, 'issueIds'> & {
  suggestions: AssigneePickerAssignee[]
  existingIssueAssignees: Map<string, string[]>
  assigneesAppliedToAll: AssigneePickerAssignee[]
  assigneesAppliedToSome: AssigneePickerAssignee[]
  repository?: {
    databaseId: number | null
    isPrivate: boolean
    isArchived: boolean
  }
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ApplyAssigneesBulkActionIssueFragment = graphql`
  fragment ApplyAssigneesBulkActionIssueFragment on Issue @inline {
    id
    assignedActors(first: 10) {
      edges {
        node {
          ... on Actor {
            __typename
            id
            login
            name
            profileResourcePath
            avatarUrl(size: 64)
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
      }
    }
    repository {
      databaseId
      isPrivate
      isArchived
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const ApplyAssigneesQuery = graphql`
  query ApplyAssigneesBulkActionQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Issue {
        ...ApplyAssigneesBulkActionIssueFragment
      }
    }
  }
`

export const ApplyAssigneesBulkAction = ({issueIds, ...rest}: ApplyAssigneesActionProps) => {
  const {nodes} = useLazyLoadQuery<ApplyAssigneesBulkActionQuery>(
    ApplyAssigneesQuery,
    {ids: issueIds},
    {fetchPolicy: 'store-only'},
  )

  // Using readInlineData here because we need to read fragment data synchronously from a dynamic
  // array of nodes. Using useFragment would violate React's rules of hooks (can't call hooks in a loop).
  // The @inline directive on the fragment enables this synchronous read pattern.
  const updatedIssueNodes = (nodes || []).filter(Boolean).map(node =>
    // eslint-disable-next-line no-restricted-syntax
    readInlineData(ApplyAssigneesBulkActionIssueFragment, node),
  ) as ApplyAssigneesBulkActionIssueFragment$data[]

  const firstIssue = updatedIssueNodes[0]
  const repository = firstIssue?.repository

  // set with all assignees of all currently selected issues
  const existingIssueAssignees = new Map<string, string[]>()
  const participants = new Map<string, AssigneePickerAssignee>()
  const selectedIssuesAssignees = updatedIssueNodes.map(node => {
    const nodeId = node?.id
    if (!nodeId) return []
    const assignees = (node.assignedActors?.edges || []).flatMap(a =>
      a?.node ? [a.node as AssigneePickerAssignee] : [],
    )

    return (
      assignees.map(assigneeData => {
        participants.set(assigneeData.id, assigneeData)
        const existingAssignees = existingIssueAssignees.get(nodeId) || []
        existingAssignees.push(assigneeData.id)
        existingIssueAssignees.set(nodeId, existingAssignees)

        return {issueId: nodeId, assigneeId: assigneeData.id, assigneeNode: assigneeData}
      }) ?? []
    )
  })
  // list of assignees applied to all selected issues
  const assigneeAppliedToAll = {} as {[key: string]: AssigneePickerAssignee}
  // list of assignees applied to some of the selected issues
  const assigneeAppliedToSome = {} as {[key: string]: AssigneePickerAssignee}
  for (const issueAssignees of selectedIssuesAssignees) {
    for (const assignee of issueAssignees) {
      if (assignee.assigneeId in assigneeAppliedToAll) continue

      if (
        selectedIssuesAssignees.every(otherIssueAssignees =>
          otherIssueAssignees.some(l => l.assigneeId === assignee.assigneeId),
        )
      ) {
        assigneeAppliedToAll[assignee.assigneeId] = assignee.assigneeNode
      } else {
        assigneeAppliedToSome[assignee.assigneeId] = assignee.assigneeNode
      }
    }
  }

  return (
    <ApplyAssigneesActionInternal
      existingIssueAssignees={existingIssueAssignees}
      suggestions={[...participants.values()]}
      assigneesAppliedToAll={Object.values(assigneeAppliedToAll)}
      assigneesAppliedToSome={Object.values(assigneeAppliedToSome)}
      repository={{
        databaseId: repository?.databaseId ?? null,
        isPrivate: repository?.isPrivate ?? false,
        isArchived: repository?.isArchived ?? false,
      }}
      {...rest}
    />
  )
}

const ApplyAssigneesActionInternal = ({
  issuesToActOn,
  disabled = false,
  nested,
  ...rest
}: ApplyAssigneesActionInternalProps) => {
  const bulkAssignButtonRef = useRef<HTMLButtonElement | HTMLLIElement | null>(null)
  // Will update both connections per issue. We have both due to the fact that we can't
  const connectionIds = issuesToActOn.reduce(
    (acc, issueId) => {
      acc[issueId] = [
        ConnectionHandler.getConnectionID(issueId, 'IssueAssignees_assignedActors'),
        ConnectionHandler.getConnectionID(issueId, 'Assignees_actionAssignees'),
      ]
      return acc
    },
    {} as {[key: string]: string[]},
  )

  const anchorElement = useCallback(
    (
      anchorProps: React.HTMLAttributes<HTMLElement> & {
        ref?: React.Ref<HTMLButtonElement | HTMLLIElement>
      },
    ) => {
      const anchorText = 'Assign'

      const {ref: anchorRef, ...restAnchorProps} = anchorProps
      const mergedRef = mergeRefs<HTMLButtonElement | HTMLLIElement>([anchorRef, bulkAssignButtonRef])

      if (nested) {
        return (
          <ActionList.Item disabled={disabled} {...restAnchorProps} role="menuitem" ref={mergedRef}>
            <ActionList.LeadingVisual>
              <PeopleIcon />
            </ActionList.LeadingVisual>
            {anchorText}
          </ActionList.Item>
        )
      }

      return (
        <Button
          ref={mergedRef}
          data-testid="bulk-set-assignee-button"
          disabled={disabled}
          leadingVisual={PeopleIcon}
          trailingVisual={TriangleDownIcon}
          {...restAnchorProps}
        >
          {anchorText}
        </Button>
      )
    },
    [disabled, nested],
  )

  const pickerProps: BulkAssigneePickerProps = {
    issuesToActOn,
    readonly: false,
    connectionIds,
    anchorElement: (props: React.HTMLAttributes<HTMLElement>) => anchorElement(props),
    nested,
    externalReturnFocusRef: bulkAssignButtonRef,
    ...rest,
  }

  return <BulkIssuesAssigneePicker {...pickerProps} />
}
